import React, { MouseEventHandler, useState, useRef } from 'react'
import classNames from 'classnames'
import { GuildRedeemTicketsCard } from 'src/molecules/GuildRedeemTicketsCard'
import { ButtonVariant, LinkButton } from '@/atoms/Button'
import { AccountCircleIcon } from '@/atoms/Icons/AccountCircleIcon'
import { ArrowLeftIcon } from '@/atoms/Icons/ArrowLeftIcon'
import { ChatBubbleOutline } from '@/atoms/Icons/ChatBubbleOutline'
import { CircleQuestionIcon } from '@/atoms/Icons/CircleQuestionIcon'
import { CreditCardIcon } from '@/atoms/Icons/CreditCardIcon'
import { GearIcon } from '@/atoms/Icons/GearIcon'
import { LogoutIcon } from '@/atoms/Icons/LogoutIcon'
import { ParentalControlsOutlineIcon } from '@/atoms/Icons/ParentalControlsOutlineIcon'
import { TicketIcon } from '@/atoms/Icons/TicketIcon'
import TriangleArrowRight from '@/atoms/Icons/TriangleArrowRight'
import { CaptionSM, TitleMD } from '@/atoms/Text'
import { paths } from '@/constants/paths'
import { useThemeContext } from '@/contexts/ThemeContext'
import { ExperimentVariation, GBFadeInExperiment } from '@/experimentation'
import { Avatar } from '@/molecules/Avatar'
import { useGuildMember } from '@/services/GuildUserService'
import { useUser } from '@/services/UserService'
import { useSafeTrack } from '@/utils/analytics'
import { useLoginUrl, useLogoutUrl } from '@/utils/auth-utils'
import { Translate, useTranslate } from '@/utils/translate/translate-client'
import { IconLink } from '../IconLink'
import { LanguageAccordion } from '../LanguageAccordion'
import { UserMenuHeader } from './UserMenuHeader'

interface Props {
  loginButtonVariant: ButtonVariant
  onClick?: MouseEventHandler
}

export const UserMenu: React.FC<Props> = ({ loginButtonVariant, onClick }) => {
  const { isLoggedIn, loading } = useUser()
  const { guildPermissions } = useGuildMember()
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const { loginUrl, trackLoginStarted } = useLoginUrl()
  const { logoutUrl, trackLogoutStarted } = useLogoutUrl()
  const { t } = useTranslate('common')
  const { isDarkMode } = useThemeContext()
  const track = useSafeTrack()
  const shareGuildCodesModalRef = useRef<HTMLDivElement | null>(null)

  const handleTrackingClick = () => {
    track('Clicked User Menu Link')
  }

  const handleClickAwayFromMenu: MouseEventHandler = (e) => {
    e.preventDefault()
    setIsMenuOpen(false)
  }

  const handleAvatarClick: MouseEventHandler = (e) => {
    handleTrackingClick()
    setIsMenuOpen((prev) => !prev)
    if (onClick) onClick(e)
  }

  const handleItemClick: MouseEventHandler = (e) => {
    handleTrackingClick()
    setIsMenuOpen(false)
    if (onClick) onClick(e)
  }

  if (loading) {
    return <div className="w-8" />
  }

  const avatarClasses = classNames(
    isDarkMode ? 'border-black ring-white' : 'border-white ring-black',
    'overflow-hidden rounded-full border-2 bg-cover ring-2 w-fit',
  )

  if (!isLoggedIn) {
    return (
      <div className="flex items-center">
        <LinkButton
          href={loginUrl}
          variant={loginButtonVariant}
          outline
          className="box-border h-8 rounded-md backdrop-blur-sm"
          onClick={() => trackLoginStarted('top-nav')}
        >
          <CaptionSM className="whitespace-nowrap">
            <Translate t={t} i18nKey="logIn">
              Log In
            </Translate>
          </CaptionSM>
        </LinkButton>
      </div>
    )
  }

  return (
    <div>
      <div className="md:mr-6">
        {isMenuOpen && (
          <div
            className={'absolute left-0 top-0 z-[9998] max-h-full min-h-screen w-screen max-w-full bg-transparent'}
            onClick={handleClickAwayFromMenu}
          ></div>
        )}
        <div className="md:relative">
          <div className={classNames(avatarClasses, 'ring-0')}>
            <Avatar onClick={handleAvatarClick} />
          </div>

          {isMenuOpen && (
            <div
              className={classNames(
                isDarkMode ? 'bg-black text-white' : 'bg-white text-black',
                'absolute right-0 top-0 z-[9999] min-h-full md:min-h-fit w-full p-4 shadow-lg md:-right-[15px] md:top-[50px] md:h-fit md:w-[390px] md:rounded-lg md:pt-0 [will-change:transform]',
              )}
            >
              <TriangleArrowRight
                color={isDarkMode ? 'black' : 'white'}
                className="absolute -top-[11px] right-6 origin-left -rotate-90"
              />
              <div onClick={handleItemClick} className="mb-4 md:hidden">
                <ArrowLeftIcon color={isDarkMode ? 'white' : 'black'} size={20} />
              </div>
              <section>
                <UserMenuHeader handleItemClick={handleItemClick} />
                <div className="-mx-2 md:mx-0">
                  <IconLink
                    href={paths.account.payments}
                    handleClick={handleItemClick}
                    linkText={t('myAccount', 'My Account')}
                  >
                    <AccountCircleIcon size={24} color1={isDarkMode ? 'black' : 'white'} color2="core-gray-600" />
                  </IconLink>
                  <IconLink
                    href={paths.account.index}
                    handleClick={handleItemClick}
                    linkText={t('membershipAndPayments', 'Membership & Payments')}
                  >
                    <CreditCardIcon size={24} color="core-gray-600" />
                  </IconLink>
                  <IconLink
                    href={paths.account.myTickets}
                    handleClick={handleItemClick}
                    linkText={t('myTickets', 'My Tickets')}
                  >
                    <TicketIcon size={24} color="core-gray-600" />
                  </IconLink>
                </div>
                {guildPermissions.hasFreeTickets && (
                  <GuildRedeemTicketsCard modalRef={shareGuildCodesModalRef} className="my-2" isDismissable />
                )}
                <GBFadeInExperiment experimentName="parental-controls" defaultValue={false}>
                  <ExperimentVariation variation={true}>
                    <IconLink
                      href={paths.account.parentalControls}
                      handleClick={handleItemClick}
                      linkText={t('parentalControlsBeta', 'Parental Controls (Beta)')}
                    >
                      <ParentalControlsOutlineIcon size={24} color="core-gray-600" />
                    </IconLink>
                  </ExperimentVariation>
                </GBFadeInExperiment>
                <div className="-mx-2 md:mx-0">
                  <IconLink
                    href={paths.account.settings}
                    handleClick={handleItemClick}
                    linkText={t('settings', 'Settings')}
                  >
                    <GearIcon size={24} color="core-gray-600" />
                  </IconLink>
                </div>
              </section>
              <section className="-mx-2 my-4 md:mx-0">
                <TitleMD weight="semibold" className="mx-2 mb-4">
                  {t('more', 'More')}
                </TitleMD>
                <IconLink
                  href={paths.external.zendesk.support}
                  handleClick={handleItemClick}
                  linkText={t('help', 'Help')}
                  isExternal={true}
                >
                  <CircleQuestionIcon size={24} color="core-gray-600" />
                </IconLink>
                <IconLink
                  href={paths.external.zendesk.contactUs}
                  handleClick={handleItemClick}
                  linkText={t('giveFeedback', 'Give Feedback')}
                  isExternal={true}
                >
                  <ChatBubbleOutline size={24} color="core-gray-600" />
                </IconLink>
                <LanguageAccordion onClose={() => setIsMenuOpen(false)} />
              </section>

              <section className="-mx-2 mt-4 md:mx-0">
                <IconLink
                  href={logoutUrl}
                  linkText={t('logoutV2', 'Logout')}
                  handleClick={(e) => {
                    trackLogoutStarted('top-nav')
                    handleItemClick(e)
                  }}
                >
                  <LogoutIcon color="core-gray-600" />
                </IconLink>
              </section>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
